// AUTO-GENERATED — DO NOT EDIT THIS FILE DIRECTLY.
//
// Synced from chains.js
// by scripts/sync_wallet_app.sh on 2026-07-17T08:09:17Z.
//
// To make changes:
//   1. Edit react/_shared/wallet-app/chains.js
//   2. Run ./scripts/sync_wallet_app.sh
//
// Editing this copy in place will be overwritten on next sync.
//
// ---------------------------------------------------------------

// chains.js
//
// Single chain registry for the Bankcoin customer apps. Mirrors the proven
// Wagmi v2 + viem chain set from react/depositor-web-v2/src/wagmi.ts and
// adds the Track-C contract address map (BridgeSender, BridgeReceiver,
// AutoMintReceiver, SwapEngine) pulled from .credentials/cpp/contracts.env.
//
// Addresses are read at build time from Vite env vars, with a hardcoded
// fallback to the current contracts.env snapshot. To rotate an address:
// either set VITE_CONTRACTS_<chainId>_<NAME> in the consumer app's .env, or
// update the fallback below and re-run scripts/sync_wallet_app.sh.

import { http } from 'wagmi';
import {
  arbitrum,
  base,
  baseSepolia,
  bsc,
  mainnet,
  optimism,
  polygon,
  sepolia,
} from 'wagmi/chains';

// Environment tier mirrors depositor-web-v2: 'local' | 'staging' | 'production'.
export const ENVIRONMENT =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ENVIRONMENT) ||
  (typeof import.meta !== 'undefined' && import.meta.env?.PROD ? 'production' : 'local');

// Production: mainnets only. Staging: + Sepolia + Base Sepolia. Local: testnets first.
export const APP_CHAINS =
  ENVIRONMENT === 'production'
    ? [mainnet, base, arbitrum, optimism, polygon, bsc]
    : ENVIRONMENT === 'staging'
      ? [mainnet, sepolia, baseSepolia, base, arbitrum, optimism, polygon, bsc]
      : [sepolia, baseSepolia, mainnet, base, arbitrum, optimism, polygon, bsc];

// Display names for the UI (chain.name from viem is sometimes the slug — we
// want consistent presentation across the three apps).
export const CHAIN_NAMES = {
  1: 'Ethereum',
  10: 'Optimism',
  56: 'BNB Chain',
  137: 'Polygon',
  8453: 'Base',
  42161: 'Arbitrum One',
  11155111: 'Sepolia',
  84532: 'Base Sepolia',
};

export const chainName = (chainId) =>
  CHAIN_NAMES[chainId] || `Chain ${chainId}`;

// Block explorer URLs — used by the StateTimeline to link tx hashes.
export const CHAIN_EXPLORERS = {
  1: 'https://etherscan.io',
  10: 'https://optimistic.etherscan.io',
  56: 'https://bscscan.com',
  137: 'https://polygonscan.com',
  8453: 'https://basescan.org',
  42161: 'https://arbiscan.io',
  11155111: 'https://sepolia.etherscan.io',
  84532: 'https://sepolia.basescan.org',
};

export const explorerUrl = (chainId, txHash) => {
  const base = CHAIN_EXPLORERS[chainId];
  if (!base || !txHash) return null;
  return `${base}/tx/${txHash}`;
};

// Testnet chain IDs — UI shows a "Testnet — no real value" pill when used.
const TESTNET_IDS = new Set([11155111, 84532]);
export const isTestnet = (chainId) => TESTNET_IDS.has(Number(chainId));

// ── Alchemy RPC + browser-safe fallbacks (same pattern as depositor-web-v2) ──
const alchemyKey =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ALCHEMY_API_KEY) || '';

const ALCHEMY_RPCS = {
  1: alchemyKey ? `https://eth-mainnet.g.alchemy.com/v2/${alchemyKey}` : '',
  10: '',
  56: '',
  137: alchemyKey ? `https://polygon-mainnet.g.alchemy.com/v2/${alchemyKey}` : '',
  8453: alchemyKey ? `https://base-mainnet.g.alchemy.com/v2/${alchemyKey}` : '',
  42161: alchemyKey ? `https://arb-mainnet.g.alchemy.com/v2/${alchemyKey}` : '',
  11155111: alchemyKey ? `https://eth-sepolia.g.alchemy.com/v2/${alchemyKey}` : '',
  84532: alchemyKey ? `https://base-sepolia.g.alchemy.com/v2/${alchemyKey}` : '',
};

const FALLBACK_RPCS = {
  1: 'https://cloudflare-eth.com',
  10: 'https://mainnet.optimism.io',
  56: 'https://bsc-dataseed.bnbchain.org',
  137: 'https://polygon-rpc.com',
  8453: 'https://mainnet.base.org',
  42161: 'https://arb1.arbitrum.io/rpc',
  11155111: 'https://rpc.sepolia.org',
  84532: 'https://sepolia.base.org',
};

export function rpcFor(chainId) {
  return ALCHEMY_RPCS[chainId] || FALLBACK_RPCS[chainId] || undefined;
}

export function buildTransports() {
  const out = {};
  for (const chain of APP_CHAINS) {
    out[chain.id] = http(rpcFor(chain.id));
  }
  return out;
}

// ── Contract address registry ────────────────────────────────────────────────

function fromEnv(chainId, name) {
  if (typeof import.meta === 'undefined' || !import.meta.env) return null;
  return import.meta.env[`VITE_CONTRACTS_${chainId}_${name}`] || null;
}

// Static snapshot — keep in sync with .credentials/cpp/contracts.env.
const STATIC = {
  // Sepolia — primary testnet
  11155111: {
    SWAP_ENGINE: '0xbC70865314cDe9DEAe10Ac99601601d2e20615E4',
    DISCOUNT_BILL: '0x61853C934c525447Ad44B1d351fddF922B7dA136',
    BRIDGE_SENDER: null,
    BRIDGE_RECEIVER: null,
    AUTO_MINT_RECEIVER: null,
    USDC: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238',
    USDT: '0x7169D38820dfd117C3FA1f22a697dBA58d90BA06',
    kUSD: '0x307d582Dc3Fc9C201E1dd3D2F12112A7EE8D30aE',
    kEUR: '0x014164919591745dD7465914e1aF6bfC4866A141',
    kJPY: '0xFc71F930e55886D9a4E0406d7D339B3949CFcfb3',
    kGBP: '0x1B2BAbEfE373B7Cc1e8488324A206bfE843e9f66',
    kCNH: '0x4F478f0278F9FBE8ac1865199583C953b49D0214',
    kCAD: '0x5d8004C01F3d86EE0c63E608792e0Fb8365D7E9c',
  },
  84532: {
    SWAP_ENGINE: null,
    DISCOUNT_BILL: null,
    BRIDGE_SENDER: null,
    BRIDGE_RECEIVER: null,
    AUTO_MINT_RECEIVER: null,
  },
  8453: {
    SWAP_ENGINE: '0x2c4Fc907c6b6326B078cC4a4FBA5410884207261',
    DISCOUNT_BILL: '0xE4A48B05Bb9656271301Ea31af8D98436c5b40Ad',
    BRIDGE_SENDER: null,
    BRIDGE_RECEIVER: null,
    AUTO_MINT_RECEIVER: null,
    USDC: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    USDT: '0xfde4C96c8593536E31F229EA8f37b2ADa2699bb2',
  },
  1: {
    SWAP_ENGINE: null,
    DISCOUNT_BILL: '0x4049F922977347fD217B266618a9F17bc97c181B',
    BRIDGE_SENDER: null,
    BRIDGE_RECEIVER: null,
    AUTO_MINT_RECEIVER: null,
    USDC: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
    USDT: '0xdAC17F958D2ee523a2206206994597C13D831ec7',
  },
  10: { SWAP_ENGINE: null, BRIDGE_SENDER: null, BRIDGE_RECEIVER: null, AUTO_MINT_RECEIVER: null },
  42161: { SWAP_ENGINE: null, BRIDGE_SENDER: null, BRIDGE_RECEIVER: null, AUTO_MINT_RECEIVER: null },
  137: { SWAP_ENGINE: null, BRIDGE_SENDER: null, BRIDGE_RECEIVER: null, AUTO_MINT_RECEIVER: null },
  56: { SWAP_ENGINE: null, BRIDGE_SENDER: null, BRIDGE_RECEIVER: null, AUTO_MINT_RECEIVER: null },
};

export function contractAddress(chainId, name) {
  const env = fromEnv(chainId, name);
  if (env) return env;
  return STATIC[chainId]?.[name] || null;
}

export function isContractDeployed(chainId, name) {
  return contractAddress(chainId, name) !== null;
}

export function chainsWithContract(name) {
  const ids = [];
  for (const chain of APP_CHAINS) {
    if (isContractDeployed(chain.id, name)) ids.push(chain.id);
  }
  return ids;
}

// ── kStable registry ─────────────────────────────────────────────────────────

export const KSTABLES = [
  { symbol: 'kUSD', ccy: 'USD', label: 'kUSD — US Dollar', decimals: 6 },
  { symbol: 'kEUR', ccy: 'EUR', label: 'kEUR — Euro', decimals: 6 },
  { symbol: 'kGBP', ccy: 'GBP', label: 'kGBP — British Pound', decimals: 6 },
  { symbol: 'kJPY', ccy: 'JPY', label: 'kJPY — Japanese Yen', decimals: 6 },
  { symbol: 'kCNH', ccy: 'CNH', label: 'kCNH — Offshore Yuan', decimals: 6 },
  { symbol: 'kCAD', ccy: 'CAD', label: 'kCAD — Canadian Dollar', decimals: 6 },
  { symbol: 'kCHF', ccy: 'CHF', label: 'kCHF — Swiss Franc', decimals: 6 },
  { symbol: 'kAUD', ccy: 'AUD', label: 'kAUD — Australian Dollar', decimals: 6 },
  // Commodities (2026-07-17) — bridgeable once wire_bridge_pair.sh has run.
  { symbol: 'kXAU', ccy: 'XAU', label: 'kXAU — Gold', decimals: 6 },
  { symbol: 'kXAG', ccy: 'XAG', label: 'kXAG — Silver', decimals: 6 },
  { symbol: 'kXPT', ccy: 'XPT', label: 'kXPT — Platinum', decimals: 6 },
  { symbol: 'kXPD', ccy: 'XPD', label: 'kXPD — Palladium', decimals: 6 },
  { symbol: 'kXCU', ccy: 'XCU', label: 'kXCU — Copper', decimals: 6 },
  { symbol: 'kXBR', ccy: 'XBR', label: 'kXBR — Brent Crude', decimals: 6 },
  { symbol: 'kXTI', ccy: 'XTI', label: 'kXTI — WTI Crude', decimals: 6 },
  { symbol: 'kXNG', ccy: 'XNG', label: 'kXNG — Natural Gas', decimals: 6 },
];

export const kStableInfo = (symbol) =>
  KSTABLES.find((k) => k.symbol === symbol) || null;

// Stablecoin underlyings we accept as mint collateral.
export const COLLATERAL_ASSETS = [
  { symbol: 'USDC', ccy: 'USD', label: 'USDC — Circle' },
  { symbol: 'USDT', ccy: 'USD', label: 'USDT — Tether' },
  { symbol: 'EURC', ccy: 'EUR', label: 'EURC — Circle' },
  { symbol: 'kUSD', ccy: 'USD', label: 'kUSD' },
  { symbol: 'kEUR', ccy: 'EUR', label: 'kEUR' },
];

// Chain icon emoji set — used by ChainPicker for a lightweight visual hint
// until we wire SVG chain logos. Falls back to a generic chain glyph.
export const CHAIN_GLYPHS = {
  1: '⟠',          // Ethereum
  10: '🔴',        // Optimism
  56: '🟡',        // BNB
  137: '🟣',       // Polygon
  8453: '🔵',      // Base
  42161: '🔷',     // Arbitrum
  11155111: '⟠',  // Sepolia
  84532: '🔵',    // Base Sepolia
};

export const chainGlyph = (chainId) => CHAIN_GLYPHS[chainId] || '🔗';
